import type { ReactElement } from 'react';
import React, { Fragment, Suspense } from 'react';

import { extract } from './../../../ricos-content/RicosContentAPI/extract-next';
import type { GalleryData_Item, RichContent } from './../../../ricos-schema/legacy/wix/rich_content/v1';
import type { RicosDocument, RicosDocumentNode } from './../../../ricos-schema/next-schema';
import { Node_Type } from './../../../ricos-schema/next-schema';
import type {
  AvailableExperiments,
  FullscreenProps,
  Helpers,
  LegacyViewerPluginConfig,
} from './../../../ricos-types';

import type { FullscreenModalItem } from './fullscreen-modal';

interface Props {
  children: (args: { config: LegacyViewerPluginConfig; onHover: () => void }) => ReactElement;
  config: LegacyViewerPluginConfig;
  helpers?: Helpers;
  content?: RichContent;
  isMobile: boolean;
  fullscreenProps?: FullscreenProps;

  experiments?: AvailableExperiments;
  jsdomMode?: boolean;
}

type ExpandModeData = {
  images: GalleryData_Item[];
  imageMap: Record<string, number>;
};

const loadFullscreenModal = () =>
  import(/* webpackChunkName: "ricos_FullscreenModalViewer"  */ './fullscreen-modal');

const FullscreenModal = React.lazy(loadFullscreenModal);

export const FullscreenProvider: React.FC<Props> = props => {
  const { children, isMobile, fullscreenProps, content } = props;
  const [state, setState] = React.useState<
    | {
        type: 'open';
        activeIndex: number;
      }
    | { type: 'closed' }
  >({ type: 'closed' });

  const expandModeData: ExpandModeData = React.useMemo(() => {
    if (content) {
      return getImagesData(content as RicosDocument);
    } else {
      return { images: [], imageMap: {} };
    }
  }, [props.content]);

  const addExpand = (config: LegacyViewerPluginConfig) => {
    const onExpand = (nodeId: string, innerIndex = 0) => {
      setState({
        type: 'open',
        activeIndex: expandModeData ? expandModeData.imageMap[nodeId] + innerIndex : 0,
      });
    };
    const newConfig = { ...config };
    const imageConfig = config['wix-draft-plugin-image'];
    const galleryConfig = config['wix-draft-plugin-gallery'];
    if (imageConfig && !imageConfig.onExpand) {
      newConfig['wix-draft-plugin-image'] = { ...imageConfig, onExpand };
    }
    if (galleryConfig && !galleryConfig.onExpand) {
      newConfig['wix-draft-plugin-gallery'] = { ...galleryConfig, onExpand };
    }
    return newConfig;
  };

  return (
    <Fragment>
      {children({ config: addExpand(props.config), onHover: () => loadFullscreenModal() })}
      {state.type === 'open' && (
        <Suspense fallback={<div />}>
          <FullscreenModal
            images={expandModeData.images}
            onClose={() => setState({ type: 'closed' })}
            index={state.activeIndex}
            isMobile={isMobile}
            {...fullscreenProps}
            jsdomMode={props.jsdomMode}
          />
        </Suspense>
      )}
    </Fragment>
  );
};

const getImagesData = (
  content: RicosDocument
): {
  images: FullscreenModalItem[];
  imageMap: Record<string, number>;
} => {
  const imagesData: { images: FullscreenModalItem[]; imageMap: Record<string, number> } = {
    images: [],
    imageMap: {},
  };
  let index = 0;
  return extract(content.nodes)
    .filter(node => [Node_Type.IMAGE, Node_Type.GALLERY].includes(node.type as any))
    .get()
    .reduce((items, node) => {
      const { images, imageMap } = items;
      const galleryItems = convertDataToGalleryItems(node);
      const currentIndex = index;
      index += galleryItems.length;
      return {
        images: [...images, ...galleryItems],
        imageMap: {
          ...imageMap,
          // @TODO: currently indexing doesn't work when nodes don't have ids
          [node.id ?? '']: currentIndex,
        },
      };
    }, imagesData);
};

const convertDataToGalleryItems = (node: RicosDocumentNode): FullscreenModalItem[] => {
  switch (node.type) {
    case Node_Type.IMAGE:
      return node.imageData.image?.src && !node.imageData?.disableExpand
        ? [
            {
              image: { media: { ...node.imageData.image } },
              disableDownload: node.imageData.disableDownload,
            },
          ]
        : [];
    case Node_Type.GALLERY: {
      return node.galleryData?.disableExpand
        ? []
        : node.galleryData.items.map((item): FullscreenModalItem => {
            // @ts-expect-error will be fixed in full migration to next-schema
            return { ...item, disableDownload: node.galleryData.disableDownload };
          });
    }
    default:
      return [];
  }
};
